import { gql, MutationResult, useMutation } from '@apollo/client';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { errorToast } from 'basics/utils/toast';
import { extractApolloErrorDetails } from 'services/graphql/extractApolloErrorDetails';
import Logger from 'services/logger';

export type ExportParticipantSignaturesResponse = {
  exportParticipantSignatures: {
    buffer: string;
    fileName: string | null;
  };
};

export type ExportParticipantSignaturesResult = {
  buffer: string;
  fileName: string | null;
} | null;

export type UseExportParticipantSignaturesMutation = [
  (eventId?: string) => Promise<ExportParticipantSignaturesResult>,
  MutationResult<ExportParticipantSignaturesResponse>,
];

export const exportParticipantSignaturesMutationCall = gql`
mutation exportParticipantSignatures($eventId: String!) {
    exportParticipantSignatures(eventId: $eventId) {
      buffer
      fileName
    }
}`;

export const useExportParticipantSignaturesMutation = (): UseExportParticipantSignaturesMutation => {
  const { t } = useTranslation();
  const [exportSignaturesMutation, state] = useMutation<ExportParticipantSignaturesResponse>(exportParticipantSignaturesMutationCall);

  const executeExportSignaturesMutation = useCallback(async (eventId?: string) => {
    try {
      if (!eventId) {
        errorToast(t('errors_standard'));
        return null;
      }
      const { data } = await exportSignaturesMutation({
        variables: { eventId },
      });

      const result = data?.exportParticipantSignatures;
      if (!result?.buffer) return null;
      return { buffer: result.buffer, fileName: result.fileName ?? null };
    } catch (error: unknown) {
      if (error instanceof Error) {
        const customContext = extractApolloErrorDetails(error);
        Logger.logError({
          error,
          customContext,
          transactionName: 'exportParticipantSignatures.ts - useExportParticipantSignaturesMutation',
        });
        errorToast(t('errors_standard'));
      }
      return null;
    }
  }, [exportSignaturesMutation, t]);

  return [executeExportSignaturesMutation, state];
};
